import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth } from '../firebase';
import axios from 'axios';
import { isNativeApp } from '../utils/isNativeApp';
import { getApiBaseUrl, getBackendBaseUrl } from '../utils/backendBaseUrl';
import { handleGoogleRedirectResult, isAccountExistsDifferentCredentialError, extractPendingFromError, savePendingGoogleCredential } from '../auth/googleAuth';
import { safeAvatarUrl } from '../utils/safeAvatarUrl';

const API = getApiBaseUrl();
// Timeout più alto in produzione per gestire cold start delle Cloud Functions
const backendBase = getBackendBaseUrl();
const isProd = backendBase.startsWith('https://sostegnopro.it') || backendBase.startsWith('https://www.sostegnopro.it');
const AUTH_VERIFY_TIMEOUT = isProd ? 25000 : 8000;
const AUTH_INIT_WATCHDOG_MS = 15000;
const AUTH_LOGS = process.env.NODE_ENV !== 'production';

function authLog(...args) {
  if (!AUTH_LOGS) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backendVerifying, setBackendVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const derivedProfile = React.useMemo(() => {
    const p = userProfile || {};
    const firstName = String(p.firstName || p.nome || '').trim();
    const lastName = String(p.lastName || p.cognome || '').trim();

    const rawDisplayName = String(p.displayName || '').trim();
    const computedDisplayName = `${firstName} ${lastName}`.trim();
    const displayName = (computedDisplayName || rawDisplayName || 'Utente').trim();

    const profilePhotoRaw = String(p.photoURL || p.photoUrl || p.photo_url || '').trim();
    const authPhotoRaw = String(currentUser?.photoURL || '').trim();
    const photoURL = safeAvatarUrl(profilePhotoRaw) || safeAvatarUrl(authPhotoRaw) || '';

    const initialsSource = (computedDisplayName || rawDisplayName || '').trim();
    const parts = initialsSource.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const second = (parts.length > 1 ? parts[parts.length - 1]?.[0] : '') || '';
    const initials = (first + second).toUpperCase() || 'U';

    return {
      firstName,
      lastName,
      displayName,
      photoURL,
      initials,
    };
  }, [userProfile, currentUser]);

  // Funzione per verificare l'utente nel backend
  const verifyUserInBackend = async (firebaseUser) => {
    setBackendVerifying(true);
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.post(
        `${API}/auth/verify`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          timeout: AUTH_VERIFY_TIMEOUT
        }
      );
      setUserProfile(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Errore verifica utente:', error);
      const statusCode = error?.response?.status;
      const detail = error?.response?.data?.detail;
      if (statusCode === 403) {
        setError(detail || 'Utente non autorizzato nel sistema');
      } else {
        setError('Impossibile verificare l\'accesso. Controlla la connessione e riprova.');
      }
      return null;
    } finally {
      setBackendVerifying(false);
    }
  };

  const refreshUserProfile = async () => {
    if (!auth?.currentUser) return null;
    if (!auth.currentUser.emailVerified) return null;
    return await verifyUserInBackend(auth.currentUser);
  };

  // Login - restituisce anche lo stato di verifica email
  const login = async (email, password) => {
    try {
      setError(null);
      authLog('[AUTH] start login');
      authLog('[AUTH] before signIn');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      authLog('[AUTH] success', userCredential?.user?.uid);
      
      // Controlla se l'email è verificata
      const isVerified = userCredential.user.emailVerified;
      setEmailVerified(isVerified);
      
      // Se email non verificata, non verificare nel backend
      if (!isVerified) {
        return { user: userCredential.user, emailVerified: false };
      }

      // La verifica backend viene gestita anche da onAuthStateChanged.
      // Non blocchiamo qui il flusso di login per evitare timeout su iOS.
      return { user: userCredential.user, emailVerified: true };
    } catch (error) {
      authLog('[AUTH] error', error);
      console.error('Errore login:', error);
      setError(error.message);
      throw error;
    } finally {
      authLog('[AUTH] finally stop loading (login fn)');
    }
  };

  // Signup (solo Firebase, profilo creato separatamente dall'admin)
  const signup = async (email, password) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Errore signup:', error);
      setError(error.message);
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setEmailVerified(false);
    } catch (error) {
      console.error('Errore logout:', error);
      throw error;
    }
  };

  // Monitor auth state
  useEffect(() => {
    let cancelled = false;
    let unsubscribe = null;

    // Warmup ping in produzione per ridurre cold start della Function
    if (isProd) {
      axios.get(`${API}/health`, { timeout: 5000 }).catch(() => {});
    }

    if (!auth) {
      setLoading(false);
      setError('Firebase non configurato. Inserisci le credenziali in firebase.js');
      return;
    }

    const watchdogId = setTimeout(() => {
      if (cancelled) return;
      authLog('[AUTH] init watchdog timeout - force stop loading');
      setError('Impossibile verificare l\'accesso. Controlla la connessione e riprova.');
      setLoading(false);
    }, AUTH_INIT_WATCHDOG_MS);

    const init = async () => {
      try {
        // In WKWebView/Capacitor la persistenza può essere delicata: proviamo IndexedDB, fallback a Local.
        try {
          await setPersistence(auth, isNativeApp() ? indexedDBLocalPersistence : browserLocalPersistence);
          authLog('[AUTH] persistence set');
        } catch (e) {
          authLog('[AUTH] persistence set failed', e);
        }

        // Finalizza eventuali redirect flow (Google sign-in/link)
        try {
          const redirectUser = await handleGoogleRedirectResult(auth);

          // In caso di linkWithRedirect, onAuthStateChanged potrebbe non scattare.
          // Aggiorniamo quindi il profilo backend appena possibile.
          if (redirectUser?.emailVerified) {
            void verifyUserInBackend(redirectUser);
          }
        } catch (e) {
          // Caso critico: email già esistente con provider diverso
          if (isAccountExistsDifferentCredentialError(e)) {
            const pending = extractPendingFromError(e);
            savePendingGoogleCredential(pending);
          }
          // Non bloccare init
          authLog('[AUTH] getRedirectResult error', e);
        }

        unsubscribe = onAuthStateChanged(auth, async (user) => {
          authLog('[AUTH] onAuthStateChanged', user ? user.uid : null);

          // L'inizializzazione auth è completata non appena Firebase ci restituisce uno stato.
          // Non blocchiamo questo passaggio su chiamate backend (che possono essere lente su iOS).
          if (!cancelled) {
            clearTimeout(watchdogId);
            setLoading(false);
          }

          try {
            if (cancelled) return;
            setCurrentUser(user);

            if (user) {
              setEmailVerified(user.emailVerified);
              if (user.emailVerified) {
                void verifyUserInBackend(user);
              }
            } else {
              setUserProfile(null);
              setEmailVerified(false);
            }
          } catch (e) {
            authLog('[AUTH] onAuthStateChanged error', e);
            console.error('Auth state error:', e);
            setError('Errore durante il controllo accesso');
          }
        });
      } catch (e) {
        authLog('[AUTH] init error', e);
        console.error('Auth init error:', e);
        setError('Errore inizializzazione autenticazione');
        clearTimeout(watchdogId);
        setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
      clearTimeout(watchdogId);
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    displayName: derivedProfile.displayName,
    photoURL: derivedProfile.photoURL,
    initials: derivedProfile.initials,
    emailVerified,
    login,
    signup,
    logout,
    refreshUserProfile,
    loading,
    backendVerifying,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};